import React from "react";
import {
  StyleSheet,
  KeyboardAvoidingView,
  View,
  Keyboard,
  Alert,
  Dimensions,
} from "react-native";
import moment from "moment";

import Utils, {
  getElementModel,
  getStorageKey,
  hasItems,
  isAndroid,
  isAppleDevice,
  isWeb,
  navigate,
} from "../../../../helpers/utils";

import AppMessages from "../../../../config/AppMessages";
import appStyles from "../../../appStyles";
import ElementItem from "../../elements/list/ElementItem";
import ElementsHelper from "../../elements/ElementsHelper";
import IntegrationsHelper from "../../integrations/IntegrationsHelper";
import JournalDialog from "../../common/JournalDialog";
import JournalHelper from "../JournalHelper";
import JournalTimePicker from "../JournalTimePicker";
import MenuEditOptionsPanel from "../../common/MenuEditOptionsPanel";
import Search from "../../common/search-bar/Search";
import TrackDetailFabButton from "./TrackDetailFabButton";
import TrackDetailHeader from "./TrackDetailHeader";
import TrackDetailIntegrations from "./TrackDetailIntegrations";
import TrackElementPicker from "./track-elements/TrackElementPicker";

import TUTORIALS_DATA from "../../tutorials/data";
import IntegrationMenuOptions from "../../integrations/common/IntegrationMenuOptions";
import appPages from "../../../../appPages";
import ManaButton from "../../common/ManaButton";

const { height: HEIGHT } = Dimensions.get("window");
const DEFAULT_URL = "rn:home_card";
const RETRY_INTEGRATIONS_SEC = 1.5;
const MAX_RETRIES = 2;

export default class TrackDetailPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;
    this.searchRef = null;
    this.integrationTimeout = null;
    this.elementsRefreshTimeout = null;

    this.state = {
      isLoading: true,
      selectedTimeslot: {},
      activities: [],
      expandedSlots: [],
      selectedSourceId: null,
      filters: {},
      showJournalDialog: null,
      showTimePicker: false,
      showIcons: true,
      minutesInterval: this.controller.state?.settings?.interval_duration,
      tutorialStep: 0,
    };
  }

  componentDidMount = async () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.refreshPage();
        },
      );
    } else {
      this.refreshPage();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route.params;
    } else {
      return this.controller.state.screenArgs?.args;
    }
  }

  refreshPage = async () => {
    const params = this.getArgs() || {};
    let { timeslot, returnUrl, sourceUrl, multiple } = params;

    this.setState((state) => ({ ...state, isLoading: true }));

    if (returnUrl == null) returnUrl = DEFAULT_URL;

    if (!timeslot) {
      return this.controller.changePage(returnUrl);
    }

    const mergedTimeslots = [];
    let prevTimeslot = null;
    const timeslots = this.controller.state.timeslots;

    const newTimeslots = JSON.parse(JSON.stringify(timeslots));
    JournalHelper.biasPredictions(this.controller, newTimeslots);
    newTimeslots.forEach((t) => {
      if (
        !JournalHelper.mergeTimeslot(
          prevTimeslot,
          t,
          false,
          0,
          this.state.expandedSlots,
          false,
        )
      ) {
        prevTimeslot = JSON.parse(JSON.stringify(t));
        mergedTimeslots.push(prevTimeslot);
      }
    });

    //const prevSlots = this.getPreviousTimeslots(timeslot, mergedTimeslots);

    // For returning to timeslot day
    if (timeslot?.time) {
      Utils.setStorageItem(
        getStorageKey(this.controller, "CONFIRMATION_DATE"),
        timeslot?.time,
      );
    }

    // Load integrations data if not coming from integration menu
    /* const integration = timeslot.hasIntegrationLoaded
      ? timeslot.integration
      : null; */
    if (!timeslot.hasIntegrationLoaded) {
      this.loadIntegrations(timeslot, 0);
    } else {
      this.setState((state) => ({ ...state, isLoading: false }));
    }

    // Init timeslots selected elements
    let elements = [];
    let elementIds = [];
    ["", 2, 3].forEach((pos) => {
      const timeslotElement = timeslot[`activity${pos}`];
      if (timeslotElement?.id) {
        const elem = ElementsHelper.findById(
          this.controller,
          timeslotElement.id,
        );
        if (elem) {
          elements.push(elem);
          elementIds.push(elem.id);
        }
      }
    });

    const prefs = this.controller.state?.settings || {};
    const showIcons = prefs.show_icons_in_track_page == null || prefs.show_icons_in_track_page;

    this.setState(
      (state) => ({
        ...state,
        selectedTimeslot: {
          ...timeslot,
          //integration,
        },
        sourceUrl,
        returnUrl,
        showIcons,
        trackMultiple: multiple || elementIds.length > 1,
        selectedElements: elements,
        selectedElementIds: new Set(elementIds),
      }),
      () => {
        // Tutorials
        this.initTutorials();
      },
    );
  };

  loadIntegrations(timeslot, retry) {
    try {
      IntegrationsHelper.getTimeslotIntegrations(
        this.controller,
        timeslot,
      ).then((int) => {
        let hasData = true;
        if (!int || !hasItems(int.activeIntegrations)) {
          if (retry < MAX_RETRIES) {
            console.log("Retrying in " + RETRY_INTEGRATIONS_SEC + " sec");
            this.integrationTimeout = setTimeout(
              () => this.loadIntegrations(timeslot, retry + 1),
              RETRY_INTEGRATIONS_SEC * 1000,
            );
          }
          hasData = false;
        }

        if (retry === 0 || hasData) {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            selectedTimeslot: {
              ...(state.selectedTimeslot || timeslot),
              integration: int,
              hasIntegrationLoaded: true,
            },
          }));
        }
      });
    } catch (e) {
      console.log(e.stack);
    }
  }

  // ------------------------------------- CONFIRM ACTIVITY -------------------------------------
  trackSimultaneously = () => {
    const { selectedTimeslot, selectedElementIds } = this.state;

    if (
      !selectedTimeslot ||
      selectedElementIds.size == 0 ||
      selectedElementIds.size > 3
    ) {
      return;
    }

    this.setState((state) => ({ ...state, isLoading: true }));
    
    JournalHelper.confirmMultipleElements(
      this.controller,
      selectedTimeslot,
      Array.from(selectedElementIds).join(","),
      this.onConfirmComplete,
    );
  };

  _updateSelectedElements = (element, is_selected) => {
    let selectedElementIds = this.state.selectedElementIds || new Set();

    // Validation error message
    if (is_selected && selectedElementIds.size >= 3) {
      this.setState((state) => ({
        ...state,
        mariMessage: {
          hasError: true,
          message: AppMessages.TrackMultipleErrorMessage,
        },
      }));
      return;
    }

    if (is_selected) {
      selectedElementIds.add(element.id);
    } else {
      selectedElementIds.delete(element.id);
    }

    this.setState((state) => ({
      ...state,
      selectedElementIds,
      mariMessage: "",
    }));
  };

  swapCurrentActivity = (activity, isSelected) => {
    const { selectedTimeslot, trackMultiple } = this.state;

    if (!selectedTimeslot || !activity) return;

    // Allow user to select up to 3 elements
    if (trackMultiple) {
      return this._updateSelectedElements(activity, isSelected);
    }

    // Save to DB
    this.confirmTimeslot({
      ...selectedTimeslot,
      activity,
    });
  };

  confirmTimeslot = (ts) => {
    const { trackMultiple } = this.state;
    
    this.controller.tracking.track("Confirm EditJournal");
    this.controller.trackConfirmTriggered = +new Date(); //temp hack to avoid multiple refresh actions

    if (trackMultiple) {
      this.trackSimultaneously();
    } else {
      if (this.elementsRefreshTimeout != null) {
        clearTimeout(this.elementsRefreshTimeout);
      }
      if (this.integrationTimeout != null) {
        clearTimeout(this.integrationTimeout);
      }

      JournalHelper.saveTimeslotToDB(
        this.controller,
        this.controller.state.timeslots || [],
        {
          timeslot: ts,
          sourceOverride: "edit",
        },
        this.onConfirmComplete,
        false
      );
    }
  };

  onConfirmComplete = () => {
    const navigation = this.props.navigation;
    if (navigation && this.state.sourceUrl === this.state.returnUrl) {
      // Refesh journal gems
      this.controller.getApiData("timeslots", {}, () => {
        navigation.goBack();
      });
    } else {
      const args = this.state.showTutorials ? { tutorialStep: 5 } : {};
      this.setState((state) => ({ ...state, isLoading: true }));
      this.controller.changePage(this.state.returnUrl, { args });
    }
  };

  goBack = () => {
    const { selectedTimeslot } = this.state;

    // User updated endTime
    if (selectedTimeslot.hasUpdatedEndTime && selectedTimeslot.activity) {
      this.confirmTimeslot(selectedTimeslot);
    } else {
      this.onConfirmComplete();
    }
  };

  // ------------------------------------- DIALOGS -------------------------------------
  updateTimePickerDuration = async (ts) => {
    const { selectedTimeslot } = this.state;
    const { startTime, endTime, startDate, endDate, time } = ts;

    this.toggleTimePickerDialog();

    // Update times
    const newTimeslot = {
      ...selectedTimeslot,
      hasUpdatedEndTime:
        endTime !== selectedTimeslot.endTime ||
        startTime != selectedTimeslot.startTime,
      time,
      startTime,
      startDate,
      endTime,
      endDate,
    };

    this.setState(
      (state) => ({
        ...state,
        selectedTimeslot: newTimeslot,
      }),
      () => {
        // Reload integrations data for the new timeslots ids
        let { timeslotIds, duration } = JournalHelper.getAffectedTimeslotIds(
          ts,
          this.controller.state.timeslots || [],
        );

        const integrationTimeslot = {
          id: timeslotIds.join(","),
          time,
          duration,
        };
        this.loadIntegrations(integrationTimeslot, 0);

        setTimeout(() => {
          // 2022.04.26 Don't open the kb after time change for mobile
          if (this.searchRef && isWeb) this.searchRef.focus();
        }, 50);
      },
    );
  };

  toggleTimePickerDialog = () => {
    const { showTimePicker, showTutorials, tutorialStep } = this.state;
    Keyboard.dismiss();

    this.setState(
      (state) => ({
        ...state,
        showTimePicker: !showTimePicker,
        mariMessage: "",
      }),
      () => {
        if (showTutorials) {
          this.loadTutorial(tutorialStep + 1);
        }
      },
    );
  };

  toggleJournalDialog = (isOpen) => {
    this.setState(
      (state) => ({
        ...state,
        showJournalDialog: isOpen,
      }),
      () => {
        if (this.searchRef && isWeb) this.searchRef.focus();
      },
    );
  };

  updateMariMessage = (mariMessage) => {
    this.setState((state) => ({ ...state, mariMessage }));
  };

  // ------------------------------------- ACTIVITIES -------------------------------------
  filterResults = (filters) => {
    this.setState((state) => ({
      ...state,
      filters: { ...filters },
    }));
  };

  addNewElement = (name = "") => {
    const { selectedTimeslot, selectedElementIds, trackMultiple, returnUrl } =
      this.state;

    const { isGrowEnd, extend_interval, extend_interval_start } =
      JournalHelper.getExtendInterval(selectedTimeslot);

    let { timeslotIds } = JournalHelper.getAffectedTimeslotIds(
      selectedTimeslot,
      this.controller.state.timeslots,
    );
    if (isGrowEnd !== 1) timeslotIds = selectedTimeslot?.id?.split(",");

    let model = Utils.getElementModel({ name });
    model.timeslot = {
      extend_interval,
      extend_interval_start,
      end_time: selectedTimeslot.endDate,
      timeslot_id: timeslotIds.join(","),
    };

    // User wants to multi-track with a new element
    if (trackMultiple) {
      model.concurrentIds = Array.from(selectedElementIds).join(",");
    }

    this.controller.changePage("feed", {
      selectedPage: `edit_element`,
      args: {
        element: model,
        returnUrl,
      },
    });
  };

  // ------------------------------------- TUTORIALS -------------------------------------
  initTutorials = () => {
    const { showTrackTutorials } = this.controller?.state?.showTutorials || {};
    if (showTrackTutorials) {
      this.loadTutorial(1);
    }
  };

  loadTutorial = (step = 1) => {
    const { selectedTimeslot } = this.state;

    const endTime =
      step == 1
        ? selectedTimeslot.endTime
        : moment(selectedTimeslot.endTime, "h:mma")
            .add(15, "minutes")
            .format("h:mma");

    const tutorial =
      step >= 0 ? TUTORIALS_DATA["track_edit"][step - 1] || {} : {};

    this.setState((state) => ({
      ...state,
      showTutorials: true,
      tutorialStep: step,
      mariMessage: tutorial.message.replace("[END_TIME]", endTime),
      mariAction: tutorial.action
        ? {
            text: tutorial.action.text,
            onPress: () => this.loadTutorial(step + 1),
          }
        : {},
    }));
  };

  // ------------------------------------- MENU OPTIONS -------------------------------------
  getMenuOptions = (element) => {
    return [
      {
        text: "Edit Element",
        action: this.editMenuElement,
      },
      {
        text:
          element.is_favorite == 1
            ? "Remove from Favorites"
            : "Mark as Favorite",
        action: this.toggleFavoriteState,
      },
      {
        text: "Archive Element",
        action: this.archiveMenuElement,
        isDelete: true,
      },
    ];
  };

  editMenuElement = () => {
    const { selectedMenuElement } = this.state;

    this.closeEditOptions();
    navigate(this.controller, "rn:edit_element", "changepage", {
      element: getElementModel(selectedMenuElement),
    });
  };

  toggleFavoriteState = () => {
    const { selectedMenuElement: item } = this.state;

    const isFavorite = item.is_favorite == 1;
    // Toggle
    item.is_favorite = isFavorite ? 0 : 1;

    this.closeEditOptions();
    this.controller.tracking.track("Edit Element");
    this.controller.cardActionWithCallback({
      action: "save",
      target: "element",
      edit_id: item.id,
      edit_is_active: true,
      edit_is_favorite: !isFavorite,
      edit_name: item.name,
    });

    this.elementsRefreshTimeout = setTimeout(() => {
      this.elementsList.refresh();
    }, 1000);
  };

  archiveMenuElement = () => {
    if (isWeb) {
      this._confirmedArchive();
      this.closeEditOptions();
      return;
    }

    Alert.alert(
      "Archive element",
      "Are you sure you want to remove this element?",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "OK",
          onPress: this._confirmedArchive,
        },
      ],
      { cancelable: true },
    );
  };

  _confirmedArchive = () => {
    const { selectedMenuElement } = this.state;
    if (!selectedMenuElement) return;

    this.closeEditOptions();
    this.controller.tracking.track("Edit Element");
    console.log("Edit Element");
    this.controller.cardActionWithCallback({
      action: "save",
      target: "element",
      edit_id: selectedMenuElement.id,
      edit_name: selectedMenuElement.name,
      edit_is_active: false,
    });

    this.elementsRefreshTimeout = setTimeout(() => {
      this.elementsList.refresh();
    }, 1000);
  };

  showEditOptions = (element) => {
    this.setState((state) => ({
      ...state,
      showEditPanel: true,
      selectedMenuElement: element,
    }));
  };

  closeEditOptions = () => {
    this.setState((state) => ({
      ...state,
      selectedMenuElement: null,
      selectedTag: null,
      showEditPanel: false,
    }));
  };

  enableMultipleTracking = () => {
    this.setState((state) => ({ ...state, trackMultiple: true }));
  };

  toggleSearchFocus = (isFocused) => {
    //if (isWeb) return;

    this.setState((state) => ({
      ...state,
      isSearchActive: isFocused,
    }));
  };

  // ------------------------------------- Integration Menu Options -------------------------------------
  toggleIntegrationOptionsMenu = (selectedIntegration) => {
    this.setState((state) => ({ ...state, selectedIntegration }));
  };

  // ------------------------------------- RENDER -------------------------------------
  render() {
    const {
      selectedTimeslot,
      filters,
      showJournalDialog,
      isLoading,
      minutesInterval,
      showTimePicker,
      showTutorials,
      tutorialStep,
      selectedElements,
      trackMultiple,
      selectedElementIds,
      selectedMenuElement,
      isSearchActive,
      showIcons,
      selectedIntegration,
    } = this.state;

    const selectedCount = selectedElementIds?.size || 0;

    const settings = this.controller.state?.settings || {};
    const initialNumToRender = isWeb
      ? settings.renderNumbers?.trackDetail?.totalResults || 20
      : 6;

    const flt =
      filters != null && filters.term != null ? filters.term.trim() : "";

    const journalContext = {
      controller: this.controller,
      swapCurrentActivity: this.swapCurrentActivity,
      addNewElement: this.addNewElement,
      showEditOptions: this.showEditOptions,
      tutorialStep,
    };

    return (
      <View style={appStyles.container}>
        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          keyboardVerticalOffset={isAndroid ? 30 : isAppleDevice ? -10 : 0}
          behavior="height"
        >
          {/* TimePicker */}
          {showTimePicker ? (
            <JournalTimePicker
              controller={this.controller}
              timeslot={selectedTimeslot}
              minutesInterval={minutesInterval}
              onClose={this.toggleTimePickerDialog}
              toggleTimePicker={this.updateTimePickerDuration}
              allTimeslots={this.controller.state.timeslots}
              updateMariMessage={this.updateMariMessage}
              showTutorials={showTutorials}
              disableStartTime={showTutorials}
            />
          ) : null}

          {/* Journal popup */}
          {showJournalDialog ? (
            <JournalDialog
              type={"timeslot"}
              item={selectedTimeslot}
              controller={this.controller}
              close={() => this.toggleJournalDialog(false)}
            />
          ) : null}

          {/* Header */}
          <TrackDetailHeader
            timeslot={selectedTimeslot}
            controller={this.controller}
            journalContext={journalContext}
            goBack={this.goBack}
            openJournalDialog={() => this.toggleJournalDialog(true)}
            toggleTimePickerDialog={this.toggleTimePickerDialog}
          />

          <View style={styles.content}>
            {showTutorials && tutorialStep == 1 ? (
              <View style={appStyles.overlay} />
            ) : null}

            <View
              style={{
                height: HEIGHT - 100,
              }}
            >
              {/* Current elements */}
              {hasItems(selectedElements)
                ? selectedElements.map((elem) => (
                    <ElementItem
                      key={elem.id}
                      item={elem}
                      controller={this.controller}
                      noIcons={true}
                      hideActions={true}
                      disabledHighlight={true}
                    />
                  ))
                : null}

              {/* Integrations info */}
              <TrackDetailIntegrations
                controller={this.controller}
                isLoading={isLoading}
                integration={selectedTimeslot?.integration}
                timeslot={selectedTimeslot}
                isSearchActive={isSearchActive}
                onLongPress={this.toggleIntegrationOptionsMenu}
              />

              {/* Choose or replace element */}
              <TrackElementPicker
                ref={(listRef) => {
                  this.elementsList = listRef;
                }}
                journalContext={journalContext}
                timeslot={selectedTimeslot}
                searchTerm={flt}
                initialNumToRender={initialNumToRender}
                trackMultiple={trackMultiple}
                enableMultipleTracking={this.enableMultipleTracking}
                selectedCount={selectedCount}
                showIcons={showIcons}
                selectedElementsIds={Array.from(
                  selectedElementIds || new Set(),
                )}
              />
            </View>
          </View>

          <View style={[appStyles.footerOptions, appStyles.footerOptionsWhite]}>
            <Search
              ref={(ref) => {
                this.searchRef = ref;
              }}
              controller={this.controller}
              placeholder="Search Elements or Create New"
              filterResults={this.filterResults}
              style={[
                isAppleDevice ? { bottom: 0 } : {},
                appStyles.footerSearch,
              ]}
              onInputFocus={() => this.toggleSearchFocus(!isSearchActive)}
            />

            {trackMultiple ? (
              <View style={[appStyles.horizontalButtons, { marginTop: 16 }]}>
                <ManaButton
                  style={[
                    appStyles.bottomButtonTwin,
                    appStyles.firstTwinButton,
                  ]}
                  styleText={appStyles.bottomButtonTwinText}
                  onPress={this.goBack}
                  text="Cancel"
                />

                <ManaButton
                  isMainButton
                  onPress={this.trackSimultaneously}
                  style={appStyles.bottomButtonTwin}
                  styleText={appStyles.bottomButtonTwinText}
                  text="Track Simultaneously"
                  disabled={selectedCount == 0}
                />
              </View>
            ) : null}
          </View>
        </KeyboardAvoidingView>

        {/* Long press elements options */}
        {selectedMenuElement ? (
          <MenuEditOptionsPanel
            isPanelActive={selectedMenuElement}
            close={this.closeEditOptions}
            options={this.getMenuOptions(selectedMenuElement)}
          />
        ) : null}

        {/* Integration long press menu options */}
        <IntegrationMenuOptions
          controller={this.controller}
          integration={selectedIntegration}
          close={() => this.toggleIntegrationOptionsMenu(null)}
          returnUrl={appPages.trackHome}
          timeslot={selectedTimeslot}
        />

        <View style={[appStyles.footerOptions]}>
          <TrackDetailFabButton
            addNewElement={this.addNewElement}
            style={{ bottom: trackMultiple ? 98 : 50 }}
            mariOptions={{
              mariMessage: this.state.mariMessage,
              hideActions: showTutorials,
              showTutorials,
            }}
          />
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    position: "relative",
    marginTop: 8,
    backgroundColor: "#FFF",
    flex: 1,
  },
});
